/* eslint-disable react/no-unused-prop-types */
import type { ImageFormat, ImageFit, ImageFocus } from '.'
import { getContentfulImageUrl } from '@helpers/getContentfulImageUrl'

type SourceProps = {
  format: ImageFormat
  src: string
  width: number | null
  breakpoint?: number
  bg?: string
  height?: number
  originalHeight?: number | null
  originalWidth?: number | null
  ratio?: number
  density?: number
  quality?: number
  fit?: ImageFit
  focus?: ImageFocus
}

const getMimeType = (format: ImageFormat) => {
  const MIME_TYPES = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    webp: 'image/webp',
    svg: 'image/svg',
  }

  return MIME_TYPES[format]
}

export const getSrcSetChunk = (options: SourceProps) => {
  const { density = 1, fit = 'thumb', src, width, originalWidth } = options
  // If we have the `originalWidth`, then let’s check if it’s more than the
  // requested `width`. If we don't have `originalWidth` then we’re allowed to
  // enlarge
  const canEnlarge = originalWidth && width ? originalWidth > width : true

  return getContentfulImageUrl({
    bg: options.bg,
    density,
    fit,
    focus: options.focus,
    format: options.format,
    height: options.height,
    quality: options.quality,
    ratio: options.ratio,
    src,
    width: canEnlarge ? width : originalWidth,
  })
}

const getSrcSet = (options: SourceProps) => {
  const lowDensitySrcSet = getSrcSetChunk({ ...options, density: 1 })
  const mediumDensitySrcSet = getSrcSetChunk({ ...options, density: 2 })

  return [lowDensitySrcSet, mediumDensitySrcSet].join(', ')
}

const Source = (props: SourceProps) => {
  const srcSet = getSrcSet(props)
  const type = getMimeType(props.format)
  const width = props.width ? props.width : undefined

  // If no breakpoint was given, then why do this at all? Because then we can
  // leverage the picture element’s ability to render WebPs and have it fallback
  // to JPG.
  if (typeof props.breakpoint !== 'number') {
    return <source srcSet={srcSet} type={type} />
  }

  return (
    <source
      media={`(min-width: ${props.breakpoint}px)`}
      srcSet={srcSet}
      type={type}
      width={width}
      height={props.height}
    />
  )
}

export { Source }
